/**
 * The recognition service over HTTP, as docs/tasks/floorplan-recognition/README.md describes it: the plans go up in one
 * multipart request, the job is then asked about until it settles, and its project is read from the address it names.
 */
import { parseJobStatus, RecognitionError, type FailureCode, type JobStatus, type RecognitionTransport } from './protocol';

interface Answer { status: number; text: string; retryAfter: string | null }
type Upload = (url: string, body: FormData, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<Answer>;

const aborted = () => new DOMException('Aborted', 'AbortError');

/** XMLHttpRequest, because fetch cannot report how much of an upload has gone. */
const xhrUpload: Upload = (url, body, onProgress, signal) => new Promise((resolve, reject) => {
  if (signal.aborted) { reject(aborted()); return; }
  const xhr = new XMLHttpRequest();
  xhr.open('POST', url);
  xhr.upload.onprogress = event => { if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total); };
  xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText, retryAfter: xhr.getResponseHeader('Retry-After') });
  xhr.onerror = () => reject(new RecognitionError('network'));
  xhr.onabort = () => reject(aborted());
  signal.addEventListener('abort', () => xhr.abort(), { once: true });
  xhr.send(body);
});

/** `Retry-After` as seconds or as a date. */
export function retryAfterMs(header: string | null, now = Date.now()): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(header);
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined;
}

const KNOWN: ReadonlySet<string> = new Set<FailureCode>(['not_recognized', 'timed_out', 'bad_image', 'too_large', 'unsupported', 'too_many', 'busy', 'server']);

/** A refused request: the service's own code when it gives one, else what the HTTP status means. */
export function failure(status: number, text: string, retryAfter: string | null): RecognitionError {
  let code: FailureCode | null = null, message: string | undefined;
  try {
    const error = (JSON.parse(text) as { error?: { code?: unknown; message?: unknown } })?.error;
    if (typeof error?.code === 'string' && KNOWN.has(error.code)) code = error.code as FailureCode;
    if (typeof error?.message === 'string') message = error.message;
  } catch { /* not JSON: the status speaks */ }
  code ??= status === 413 ? 'too_large' : status === 415 ? 'unsupported' : status === 422 ? 'bad_image'
    : status === 429 || status === 503 ? 'busy' : 'server';
  return new RecognitionError(code, message ?? `HTTP ${status}`, retryAfterMs(retryAfter));
}

function answer({ status, text, retryAfter }: Answer): JobStatus {
  if (status < 200 || status >= 300) throw failure(status, text, retryAfter);
  let body: unknown;
  try { body = JSON.parse(text); } catch { throw new RecognitionError('server', 'Not JSON'); }
  const job = parseJobStatus(body);
  const wait = retryAfterMs(retryAfter);
  return wait === undefined ? job : { ...job, retryAfterMs: wait };
}

export function createHttpTransport(base: string, options: { fetch?: typeof fetch; upload?: Upload } = {}): RecognitionTransport {
  const root = base.replace(/\/+$/, '');
  const request = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const upload = options.upload ?? xhrUpload;
  const jobUrl = (id: string) => `${root}/plans/recognition/${encodeURIComponent(id)}`;
  const get = async (url: string, signal: AbortSignal): Promise<Response> => {
    try {
      return await request(url, { signal, cache: 'no-store' });
    } catch (error) {
      if (signal.aborted) throw aborted();
      throw error instanceof RecognitionError ? error : new RecognitionError('network', String(error));
    }
  };
  return {
    demo: false,
    async start({ floors, culture }, onUpload, signal) {
      const body = new FormData();
      floors.forEach((floor, index) => body.append('floor', floor.blob, `floor-${index + 1}.jpg`));
      body.append('culture', culture);
      return answer(await upload(`${root}/plans/recognition`, body, onUpload, signal));
    },
    async status(id, signal) {
      const response = await get(jobUrl(id), signal);
      return answer({ status: response.status, text: await response.text(), retryAfter: response.headers.get('Retry-After') });
    },
    async project(job, signal) {
      if (!job.result) throw new RecognitionError('server', 'The job has no project');
      const response = await get(new URL(job.result.projectUrl, root + '/').href, signal);
      const text = await response.text();
      if (!response.ok) throw failure(response.status, text, response.headers.get('Retry-After'));
      if (!/<Project[\s>]/.test(text)) throw new RecognitionError('server', 'Not a project');
      return text;
    },
    cancel(id) {
      void request(jobUrl(id), { method: 'DELETE', keepalive: true }).catch(() => {});
    },
  };
}
